import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { SessionUser } from '@visep/shared-types/auth';
import type { ModuleCode } from '@visep/shared-types/common';
import { apiAuthed, bindOnExpired, sessionRefresh, setSessionCsrf } from '@/lib/api-authed';

type AuthStatus = 'loading' | 'authed' | 'anon';

interface AuthState {
  status: AuthStatus;
  user: SessionUser | null;
  enabledModules: ModuleCode[];
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<string | undefined>;
  logout: () => Promise<void>;
}

const ANON: AuthState = { status: 'anon', user: null, enabledModules: [] };

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: 'loading',
    user: null,
    enabledModules: [],
  });

  useEffect(() => {
    bindOnExpired(() => {
      setSessionCsrf(null);
      setState(ANON);
    });
    let alive = true;
    (async () => {
      // sessionRefresh é deduplicado (compartilha 1 request entre o double-invoke
      // do StrictMode) — evita rotacionar o refresh token 2x e revogar a família.
      const ok = await sessionRefresh();
      if (!ok) {
        setSessionCsrf(null);
        if (alive) setState(ANON);
        return;
      }
      try {
        const { user, enabledModules } = await apiAuthed.users.getMe();
        if (alive) setState({ status: 'authed', user, enabledModules });
      } catch {
        if (alive) setState(ANON);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  async function login(email: string, password: string): Promise<string | undefined> {
    const { user, csrfToken, passwordChangeToken } = await apiAuthed.auth.login({ email, password });
    setSessionCsrf(csrfToken);
    if (passwordChangeToken) {
      return passwordChangeToken;
    }
    const me = await apiAuthed.users.getMe();
    setState({ status: 'authed', user, enabledModules: me.enabledModules });
    return undefined;
  }

  async function logout(): Promise<void> {
    try {
      await apiAuthed.auth.logout();
    } catch {
      // logout best-effort; limpamos o estado local de qualquer forma
    }
    setSessionCsrf(null);
    setState(ANON);
  }

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function canMutate(rol: SessionUser['rol'] | undefined): boolean {
  return rol === 'Superadministrador' || rol === 'Administrador';
}

// Operar caja (abrir/cerrar, registrar ingreso/egreso) é permitido a qualquer rol
// com acesso ao módulo finanzas — a página já é module-gated e o backend valida o
// scope da caja (404 fora do scope, ADR-024). Distingue-se de canMutate (CRUD
// admin-only): o Operador atribuído opera a sua caja, mas não cria/edita/elimina cajas.
export function canOperateFinanzas(rol: SessionUser['rol'] | undefined): boolean {
  return rol != null;
}
